import React, { useEffect, useState } from "react";
import { NavLink, useRouteMatch } from "react-router-dom";
import { makeStyles, Tooltip, Box, withStyles } from "@material-ui/core";
import { FaHandshake } from "react-icons/fa";
import { BsArrowsAngleContract } from "react-icons/bs";
import {
  RiBubbleChartLine,
  RiHistoryLine,
  RiPriceTag3Line,
  RiCalendar2Line,
} from "react-icons/ri";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import { Auth } from "aws-amplify";
import { ReactComponent as TpoLogo } from "resources/tpo-logo.svg";
import { ReactComponent as Assortment } from "resources/assortment-logo.svg";
import { primaryColor } from "resources/constants";
import Button from "@material-ui/core/Button";
import RecommendationDialogComponent from "app/recommendation-engine/RecommendationDialogComponent";
import { recommendationGetActionsRecommend } from "lib/requests/recommendationEngine";
import _ from "lodash";
import {
  setCacheScenario,
  setSelectedScenario,
  setReferenceScenario,
  setCacheReferenceScenario,
} from "app/scenarios/duck/slices";
import { getCycleTypeId } from "lib/utils/cycles";
import { changeSelectedScenarioAsyncAction } from "app/scenarios/duck/actions";
import ProfileComponent from "app/profile/ProfileComponent";
import athenaLogo from "resources/logo-no-letters.png";
import { useSelector, useDispatch } from "react-redux";
import { currentDefaultScenario } from "lib/utils/scenarios";
import mixpanelTrack from "app/common/MixpanelComponent";
import AthenaMindIcon from "./AthenaMindIcon";

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    top: "0",
    left: "0",
    position: "fixed",
    backgroundColor: "#181E2A",
    zIndex: "4",
    [theme.breakpoints.up("xs")]: {
      visibility: "hidden",
      width: "0px",
    },
    [theme.breakpoints.up("sm")]: {
      visibility: "hidden",
      width: "0px",
    },
    [theme.breakpoints.up("md")]: {
      visibility: "hidden",
      width: "0px",
    },
    [theme.breakpoints.up("lg")]: {
      visibility: "visible",
      width: "70px",
    },
  },
  icon: {
    marginTop: "18px",
    width: "22px",
    height: "22px",
    color: "white",
    "& > path": {
      stroke: "white",
      fill: "white",
    },
  },
  navIcon: {
    color: "inherit",
    marginTop: "0px",
  },
  bottomIconGroup: {
    position: "absolute",
    bottom: 20,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  topIconGroup: {
    marginTop: "28px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  link: {
    color: "white",
    width: "100%",
    textDecoration: "none",
    padding: "14px 0px",
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    "&:hover": {
      backgroundColor: "#474A64",
    },
  },
  activeLink: {
    boxShadow: "0 2px 3px 0px black",
    backgroundColor: primaryColor,
    "&:hover": {
      cursor: "normal",
      backgroundColor: primaryColor,
    },
  },
  logo: {
    width: "50px",
    display: "block",
    margin: "0 auto",
    marginTop: "30px",
  },
  linkDisabled: {
    color: "gray",
    width: "100%",
    textDecoration: "none",
    padding: "15px 0px",
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
    "&:hover": {
      backgroundColor: "#474A64",
    },
  },
  iconDisabled: {
    width: "22px",
    height: "22px",
    color: "gray",
    // "& > path": {
    //   stroke: "gray",
    //   fill: "gray",
    // },
  },
  tooltipTitle: {
    fontWeight: "bold",
    fontSize: "11px",
  },
  tooltipContainer: { padding: "5px" },
}));

const StyledTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: "#484B5C",
  },
}))(Tooltip);

function DesktopSidebarComponent() {
  const classes = useStyles();
  const { url } = useRouteMatch();
  const dispatch = useDispatch();

  const catalogData = useSelector((state) => state.catalog);
  const scenariosSlice = useSelector((state) => state.scenarios);
  const cycleSlice = useSelector((state) => state.cycle);
  const areasData = useSelector((state) => state.areas);
  const retailersData = useSelector((state) => state.retailers);
  const user = useSelector((state) => state.auth.user);
  const storeTenant = useSelector((state) => state.auth.tenant);
  const configCatSlice = useSelector((state) => state.auth.configCatUser);
  const competitors = useSelector((state) =>
    _.filter(state.companies, (company) => company.is_client_company === false)
  );
  const [actionsRecommendWithLabels, setActionsRecommendWithLabels] = useState(
    []
  );
  const [isErrorGettingData, setIsErrorGettingData] = useState(false);
  const [
    isRecommendationDialogOpen,
    setIsRecommendationDialogOpen,
  ] = React.useState(false);

  const handleRecommendationDialogClose = () => {
    setIsRecommendationDialogOpen(false);
  };

  const handleRecommendationDialogOpen = () => {
    setIsRecommendationDialogOpen(true);
  };

  const handleOpenAthenaMind = async () => {
    try {
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      const accessToken = session.getAccessToken().getJwtToken();
      const refreshToken = session.getRefreshToken().getToken();

      const tenant = storeTenant;
      const scenario = scenariosSlice.selectedScenario;

      const athenaMindUrl = process.env.REACT_APP_ATHENAMIND_URL;
      const ssoWindow = window.open(`${athenaMindUrl}/sso`, "_blank");

      const isOriginAllowed = (origin, allowedOrigins) => {
        return allowedOrigins.some((allowedOrigin) => {
          if (
            typeof allowedOrigin === "string" &&
            allowedOrigin.includes("*")
          ) {
            const regexStr = `^${allowedOrigin
              .replace(/\./g, "\\.")
              .replace(/\*/g, "[a-zA-Z0-9-.]+")}$`;
            return new RegExp(regexStr).test(origin);
          }
          return allowedOrigin === origin;
        });
      };

      const handleMessage = (event) => {
        const envAllowedOrigins = process.env
          .REACT_APP_ATHENAMIND_ALLOWED_ORIGINS
          ? process.env.REACT_APP_ATHENAMIND_ALLOWED_ORIGINS.split(
              ","
            ).map((o) => o.trim())
          : [];
        const allowedOrigins = [athenaMindUrl, ...envAllowedOrigins].filter(
          Boolean
        );
        if (!isOriginAllowed(event.origin, allowedOrigins)) return;

        if (event.data?.type === "SSO_READY") {
          const messageContent = {
            type: "SSO_PAYLOAD",
            payload: {
              idToken,
              accessToken,
              refreshToken,
              tenant: tenant
                ? {
                    id: tenant.id,
                    mysql_database: tenant.mysql_database,
                    company: tenant.company,
                  }
                : null,
              scenario: scenario
                ? {
                    scenario_id: scenario.scenario_id,
                    name: scenario.name,
                    cycle_type: scenario.cycle_type,
                  }
                : null,
            },
          };
          ssoWindow.postMessage(messageContent, event.origin);

          window.removeEventListener("message", handleMessage);
        }
      };

      window.addEventListener("message", handleMessage);

      setTimeout(() => {
        window.removeEventListener("message", handleMessage);
      }, 30000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error opening Athena Mind:", error);
    }
  };

  const handleCurrentScenario = (cycleLabelType) => {
    // If we don't have scenario set in cache means that we don't have scenario selected for the cycleType specified.
    if (_.isEmpty(scenariosSlice.setCacheScenario[cycleLabelType])) {
      const selectedScenarioTotal = currentDefaultScenario(
        cycleLabelType,
        scenariosSlice.totalsByScenarioId,
        cycleSlice
      );
      // Finally, we need to request the scenario estimations to the server and set the current scenario selected and set the specific scenario type selected.
      dispatch(
        changeSelectedScenarioAsyncAction(selectedScenarioTotal?.scenario_id)
      );
      dispatch(
        setCacheScenario({
          [cycleLabelType]: selectedScenarioTotal,
        })
      );
      dispatch(
        setCacheReferenceScenario({
          [cycleLabelType]: selectedScenarioTotal,
        })
      );
      // If we have scenario selected for this cycle and the scenario selected is not equal to the cache scenario we need to change the selected scenario.
    } else if (
      scenariosSlice.setCacheScenario[cycleLabelType].scenario_id !==
      scenariosSlice.selectedScenario.scenario_id
    ) {
      dispatch(
        setSelectedScenario(scenariosSlice.setCacheScenario[cycleLabelType])
      );
      dispatch(
        setReferenceScenario(
          scenariosSlice.setCacheReferenceScenario[cycleLabelType]
        )
      );
    }
  };
  const isCycleTypeExist = (condition) => {
    return (
      _.find(
        cycleSlice.list,
        (cycle) => cycle.cycle_type_id === condition[0]?.id
      ) !== undefined
    );
  };

  const ifTypePricingExist = isCycleTypeExist(
    getCycleTypeId("Pricing", cycleSlice)
  );

  const ifTypeTpoExist = isCycleTypeExist(
    getCycleTypeId("Trade Promotions", cycleSlice)
  );

  const handleMultipleCycles = () => {
    mixpanelTrack(`Pricing module visited`);
    if (ifTypePricingExist) {
      handleCurrentScenario("Pricing");
    } else {
      handleCurrentScenario("Pricing 2.0");
    }
  };

  const handleTpoOrDailytpCycles = () => {
    if (_.isEmpty(scenariosSlice.setCacheScenario["Daily Trade Promotions"])) {
      handleCurrentScenario("Trade Promotions");
    } else {
      handleCurrentScenario("Daily Trade Promotions");
    }
  };

  useEffect(() => {
    const getActionsRecommend = async () => {
      const params = {
        path: user.username,
      };

      const differentFilters = [
        {
          id: "product",
          array: catalogData.products,
        },
        {
          id: "area",
          array: areasData,
        },
        {
          id: "retailer",
          array: retailersData,
        },
        {
          id: "line",
          array: catalogData.lines,
        },
        {
          id: "category",
          array: catalogData.categories,
        },
      ];

      const actionsRecommend = await recommendationGetActionsRecommend(params)
        .then((response) => {
          setIsErrorGettingData(false);
          if (response.data?.status_code === 404) {
            return response.data.message;
          }
          return response.data;
        })
        .catch(() => {
          setIsErrorGettingData(true);
        });
      /* eslint-disable no-param-reassign */
      if (
        !isErrorGettingData &&
        actionsRecommend &&
        Array.isArray(actionsRecommend)
      ) {
        actionsRecommend.forEach((action) => {
          differentFilters.forEach((filter) => {
            if (action[`${filter.id}_id`] !== null) {
              const findLabel = _.find(filter.array, (item) => {
                return item.id === action[`${filter.id}_id`];
              });
              if (findLabel?.label) {
                action[`${filter.id}_label`] = findLabel.label;
              } else {
                action[`${filter.id}_label`] = null;
              }
            } else {
              action[`${filter.id}_label`] = null;
            }
          });
        });
        /* eslint-enable no-param-reassign */
      }
      setActionsRecommendWithLabels(actionsRecommend);
    };
    if (isRecommendationDialogOpen) getActionsRecommend();
  }, [isRecommendationDialogOpen]);

  const textTitle = (title, phraseOne, phraseTwo, phraseThree, phraseFour) => (
    <div className={classes.tooltipContainer}>
      <div className={classes.tooltipTitle}>{title}</div>
      <div>{phraseOne}</div>
      <div>{phraseTwo}</div>
      <div>{phraseThree}</div>
      <div>{phraseFour}</div>
    </div>
  );
  const ifTypeAnnualExist = isCycleTypeExist(
    getCycleTypeId("Pricing 2.0", cycleSlice)
  );
  const ifTypeDailyTpExist = isCycleTypeExist(
    getCycleTypeId("Daily Trade Promotions", cycleSlice)
  );

  let competitorsLink = (
    <StyledTooltip
      title={textTitle("Competitors")}
      placement="right"
      data-testid="Competitors"
    >
      <Box className={classes.linkDisabled}>
        <BsArrowsAngleContract
          data-testid="competitorsIcon"
          className={`${classes.iconDisabled} ${classes.navIcon}`}
        />
      </Box>
    </StyledTooltip>
  );
  if (_.isEmpty(competitors) === false) {
    competitorsLink = (
      <StyledTooltip
        title={textTitle(
          "Competitors",
          "Discover how your promotion",
          "strategy will be affected",
          " by your competitors."
        )}
        placement="right"
        data-testid="Competitors"
      >
        <NavLink
          to={`${url}/competitors`}
          className={classes.link}
          activeClassName={classes.activeLink}
        >
          <BsArrowsAngleContract
            data-testid="competitorsIcon"
            className={`${classes.icon} ${classes.navIcon}`}
          />
        </NavLink>
      </StyledTooltip>
    );
  }

  return (
    <div className={classes.root}>
      <NavLink to={`${url}`} exact data-testid="athenaNavLink">
        <img
          data-testid="athenaLogo"
          className={classes.logo}
          src={athenaLogo}
          alt="Athena logo"
        />
      </NavLink>
      <div className={classes.topIconGroup}>
        {ifTypeTpoExist || ifTypeDailyTpExist ? (
          <StyledTooltip
            title={textTitle(
              "TPo",
              "Accelerate your sales and",
              "generate additional volume",
              "& margin thanks to a wise",
              "investment optimization"
            )}
            placement="right"
            data-testid="Tpo"
          >
            <NavLink
              to={`${url}`}
              exact
              className={classes.link}
              activeClassName={classes.activeLink}
              onClick={() => handleTpoOrDailytpCycles()}
            >
              <TpoLogo
                component={NavLink}
                className={`${classes.icon} ${classes.navIcon}`}
                fill="white"
                data-testid="tpoIcon"
              />
            </NavLink>
          </StyledTooltip>
        ) : (
          <StyledTooltip
            title={textTitle(
              "TPo",
              "There are no promotional",
              "cycles uploaded"
            )}
            placement="right"
            data-testid="Tpo"
          >
            <Box className={classes.linkDisabled}>
              <TpoLogo
                component={NavLink}
                className={`${classes.iconDisabled} ${classes.navIcon}`}
                fill="gray"
                data-testid="tpoIcon"
              />
            </Box>
          </StyledTooltip>
        )}
        {ifTypePricingExist || ifTypeAnnualExist ? (
          <StyledTooltip
            title={textTitle(
              "Pricing",
              " Find the price combination",
              "that optimizes your volume",
              "& margin subject to your",
              "operational restrictions"
            )}
            placement="right"
            data-testid="PriceScenarios"
          >
            <NavLink
              to={`${url}/pricing`} // To be changed
              exact
              className={classes.link}
              activeClassName={classes.activeLink}
              onClick={() => handleMultipleCycles()}
            >
              <RiPriceTag3Line
                data-testid="priceScenariosIcon"
                component={NavLink}
                className={`${classes.icon} ${classes.navIcon}`}
                color="white"
              />
            </NavLink>
          </StyledTooltip>
        ) : (
          <StyledTooltip
            title={textTitle(
              "Pricing",
              "There are no pricing",
              "cycles uploaded"
            )}
            placement="right"
            data-testid="PriceScenarios"
          >
            <Box className={classes.linkDisabled}>
              <RiPriceTag3Line
                data-testid="priceScenariosIcon"
                component={NavLink}
                className={`${classes.iconDisabled} ${classes.navIcon}`}
                color="gray"
              />
            </Box>
          </StyledTooltip>
        )}

        {competitorsLink}

        <StyledTooltip
          title={textTitle(
            "Elasticity",
            "Own & Cross predictive",
            "elasticities. Unravel the",
            "interrelationships between",
            "your products"
          )}
          placement="right"
          data-testid="Elasticity"
        >
          <NavLink
            to={`${url}/elasticity`}
            className={classes.link}
            activeClassName={classes.activeLink}
          >
            <RiBubbleChartLine
              data-testid="elasticityIcon"
              className={`${classes.icon} ${classes.navIcon}`}
            />
          </NavLink>
        </StyledTooltip>
        <StyledTooltip
          title={textTitle(
            "History",
            "One source of truth,",
            "harmonized consumption &",
            "shipment databases. Avoid",
            "tactics that didn’t work"
          )}
          placement="right"
          data-testid="History"
        >
          <NavLink
            to={`${url}/history`}
            className={classes.link}
            activeClassName={classes.activeLink}
          >
            <RiHistoryLine
              data-testid="historyIcon"
              className={`${classes.icon} ${classes.navIcon}`}
            />
          </NavLink>
        </StyledTooltip>
        <StyledTooltip
          title={textTitle(
            "Demand Planning",
            "Efficient production scheduling",
            "for the next 2 years.",
            " Make just what will be sold"
          )}
          placement="right"
          data-testid="DemandPlanning"
        >
          <NavLink
            to={`${url}/demand`}
            className={classes.link}
            activeClassName={classes.activeLink}
          >
            <RiCalendar2Line
              className={`${classes.icon} ${classes.navIcon}`}
              data-testid="demandPlanningIcon"
            />
          </NavLink>
        </StyledTooltip>
        {configCatSlice?.features?.athenamind ? (
          <StyledTooltip
            title={textTitle(
              "Athena Mind",
              "Open current context in",
              "Athena Mind AI assistant"
            )}
            placement="right"
            data-testid="AthenaMind"
          >
            <Button onClick={handleOpenAthenaMind} className={classes.link}>
              <AthenaMindIcon
                className={`${classes.icon} ${classes.navIcon}`}
              />
            </Button>
          </StyledTooltip>
        ) : null}
        <StyledTooltip
          title={textTitle("Assortment", "Available soon")}
          placement="right"
          data-testid="Assortment"
        >
          <Box className={classes.linkDisabled}>
            <Assortment
              fill="gray"
              className={`${classes.iconDisabled} ${classes.navIcon}`}
              data-testid="assortmentIcon"
            />
          </Box>
        </StyledTooltip>
        <StyledTooltip
          title={textTitle("Newscast", "Available soon")}
          placement="right"
          data-testid="Newscast"
        >
          <Box className={classes.linkDisabled}>
            <HiOutlineSpeakerphone
              className={`${classes.iconDisabled} ${classes.navIcon}`}
              data-testid="newscastIcon"
            />
          </Box>
        </StyledTooltip>
        <RecommendationDialogComponent
          open={isRecommendationDialogOpen}
          handleClose={handleRecommendationDialogClose}
          actionsRecommend={actionsRecommendWithLabels}
          isErrorGettingData={isErrorGettingData}
        />
        {configCatSlice?.features?.IsRecommendationEngineEnabled ? (
          <StyledTooltip title={textTitle("Recommendation ")} placement="right">
            <Button
              onClick={() => handleRecommendationDialogOpen()}
              className={classes.link}
              activeClassName={classes.activeLink}
            >
              <FaHandshake className={`${classes.icon} ${classes.navIcon}`} />
            </Button>
          </StyledTooltip>
        ) : null}
      </div>
      <div className={classes.bottomIconGroup}>
        <ProfileComponent className={classes.icon} />
      </div>
    </div>
  );
}

export default DesktopSidebarComponent;
